// src/ui/VibePoll.jsx — 📊 a poll in the Vibes feed, and the bars that appear
// once you have answered it.
//
// 🟢 THE PHONE'S CARD, IN A BROWSER. Same shape, same rules, same wording — a
//    poll answered on one and reopened on the other has to read identically or
//    the two look like different features that happen to share a question.
//
// 🟥 THE PERCENTAGES DO NOT APPEAR UNTIL YOU HAVE VOTED. Showing them first
//    turns every poll into a survey of what the majority already thinks, and
//    people answer differently once they know. Chat polls behave the same way.
//
// 🟥 AND THE DENOMINATOR IS PEOPLE, NOT VOTES CAST — which is NOT what
//    PollBubble.jsx does for chat polls. That one divides by the leading
//    option's count, so its winner is always exactly 100%: a bar chart wearing
//    a percent sign. Here a multiple-choice poll may total more than 100, and
//    that is correct — six in ten wanting A and five in ten wanting B is not a
//    contradiction when they were allowed to want both. Do not "unify" these.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Check } from './icons';
import {
  pollOptionsOf, applyDelta, tallyOf, haveIVoted, pctOf, isPollClosed, castVote,
  optionsToShow,
} from '../services/vibePoll';

export default function VibePoll({ post, myUid, onVoted }) {
  const [local, setLocal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  /* 📡 THE POLL WATCHES ITS OWN DOCUMENT — the same listener the phone's card
     grew, and for the same reason: a poll drawn from a list fetched once shows
     the tally as it was when the page loaded, so somebody else's vote never
     arrives. Kept identical on both clients so a poll cannot be live in one
     place and a photograph in the other. */
  const [live, setLive] = useState(null);
  const postId = post?.id ? String(post.id) : '';
  useEffect(() => {
    if (!postId) return undefined;
    return onSnapshot(
      doc(db, 'posts', postId),
      (snap) => {
        const data = snap.exists() ? snap.data() : null;
        if (data && Array.isArray(data.pollOptions)) setLive(pollOptionsOf(data));
      },
      () => {},
    );
  }, [postId]);

  const options = useMemo(
    () => optionsToShow({ local, live, post, uid: myUid }),
    [local, live, post, myUid],
  );
  const voters = useMemo(() => tallyOf(options).votedUsers.length, [options]);
  const iVoted = haveIVoted(options, myUid);
  const closed = isPollClosed(post);
  const multiple = post?.pollMultiple === true;

  const pick = useCallback(async (optionId) => {
    if (!myUid || busy || closed) return;
    setErr('');
    const already = options.find((o) => o.id === optionId)?.votes?.includes(myUid);
    /* 🟢 PAINT FIRST. The server decides, but a poll that waits for a round trip
       before it moves reads as broken. */
    setLocal(applyDelta(options, optionId, already ? 'remove' : 'add', myUid, multiple));
    setBusy(true);
    const res = await castVote(String(post.id), optionId);
    setBusy(false);
    if (res.ok && Array.isArray(res.options)) {
      setLocal(res.options);
      onVoted?.(String(post.id), {
        pollOptions: res.options, totalVotes: res.totalVotes, votedUsers: res.votedUsers,
      });
    } else {
      /* 🟥 A FAILED VOTE TAKES THE PAINT BACK. Leaving the bar up is worse than
         never drawing it: the person believes they voted, closes the tab, and
         finds their answer gone. */
      setLocal(null);
      setErr(res.error === 'poll_closed' ? 'This poll has closed.' : 'Your vote did not go through.');
    }
  }, [myUid, busy, closed, options, multiple, post?.id, onVoted]);

  if (!post) return null;
  const question = String(post.pollQuestion || '').trim();

  return (
    <div className="vb-poll">
      {question ? <div className="vb-poll-q">{question}</div> : null}

      {options.map((o) => {
        const chosen = (o.votes || []).includes(myUid);
        const pct = pctOf(o, voters);
        return (
          <button
            key={o.id}
            type="button"
            className={`vb-poll-opt ${chosen ? 'on' : ''}`}
            disabled={busy || closed || !myUid}
            onClick={() => pick(o.id)}
          >
            {/* 🟥 THE FILL IS ONLY DRAWN ONCE THE PERSON HAS ANSWERED. A bar at
                0% would still leak the shape of a result they have not earned. */}
            {iVoted ? <span className="vb-poll-fill" style={{ width: `${pct}%` }} /> : null}
            <span className="vb-poll-row">
              {chosen ? <Check width={13} height={13} /> : null}
              <span className="vb-poll-text">{o.text}</span>
              {iVoted ? <span className="vb-poll-pct">{pct}%</span> : null}
            </span>
          </button>
        );
      })}

      <div className="vb-poll-foot">
        {voters === 0 ? 'No votes yet' : `${voters} ${voters === 1 ? 'vote' : 'votes'}`}
        {closed ? ' · closed' : ''}
        {!iVoted && !closed && myUid ? ' · click to vote' : ''}
        {/* 🟢 SAID ONLY WHERE IT MATTERS. On a multiple-choice poll the
            percentages can total more than 100, which reads as an error unless
            somebody explains it; on a single-choice poll the same sentence
            would only be confusing. */}
        {iVoted && multiple && voters > 0 ? ' · more than one answer allowed' : ''}
      </div>

      {err ? <div className="vb-poll-err">{err}</div> : null}
    </div>
  );
}
